import { strToU8, zipSync } from "fflate";

const COLORS = Object.freeze({
  green: "0B5D45",
  greenDark: "083D30",
  greenLight: "EAF4EF",
  greenPale: "F5F9F7",
  gold: "D7A329",
  goldLight: "FFF6DF",
  ink: "15221D",
  gray: "66746E",
  line: "D7E1DC",
  red: "C93F3F",
  blue: "3978A8",
  teal: "2A8C78",
});

const CHART_COLORS = [
  COLORS.green,
  COLORS.gold,
  COLORS.blue,
  COLORS.teal,
  "7A5C99",
  "C86F3C",
  "5C7A67",
  COLORS.red,
];

function xml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function cleanName(value, fallback = "ورقة") {
  const name = String(value || fallback)
    .replace(/[\\/?*:[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return (name || fallback).slice(0, 31);
}

function uniqueSheetNames(sheets) {
  const used = new Set();
  return sheets.map((sheet, index) => {
    const base = cleanName(sheet.name, `ورقة ${index + 1}`);
    let name = base;
    let suffix = 2;
    while (used.has(name.toLowerCase())) {
      const tail = ` ${suffix}`;
      name = `${base.slice(0, 31 - tail.length)}${tail}`;
      suffix += 1;
    }
    used.add(name.toLowerCase());
    return { ...sheet, name };
  });
}

function colName(index) {
  let value = index + 1;
  let output = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    output = String.fromCharCode(65 + remainder) + output;
    value = Math.floor((value - 1) / 26);
  }
  return output;
}

function cellRef(row, column) {
  return `${colName(column)}${row}`;
}

function excelSerial(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.getTime() / 86400000 + 25569;
}

function numeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function styleFor(type, alternate = false) {
  const base = {
    text: 5,
    integer: 6,
    number: 6,
    currency: 7,
    percentage: 8,
    date: 9,
    datetime: 10,
    boolean: 5,
  }[type] || 5;
  if (!alternate) return base;
  return { 5: 11, 6: 12, 7: 13, 8: 14, 9: 19, 10: 20 }[base] || 11;
}

function cellXml(row, column, value, styleId = 0, type = "text") {
  const ref = cellRef(row, column);
  if (value === null || value === undefined || value === "") {
    return `<c r="${ref}" s="${styleId}"/>`;
  }
  if (type === "date" || type === "datetime") {
    const serial = excelSerial(value);
    if (serial !== null) return `<c r="${ref}" s="${styleId}"><v>${serial}</v></c>`;
  }
  if (["integer", "number", "currency", "percentage"].includes(type)) {
    const parsed = numeric(value);
    if (parsed !== null) {
      const stored = type === "percentage" ? parsed / 100 : parsed;
      return `<c r="${ref}" s="${styleId}"><v>${stored}</v></c>`;
    }
  }
  if (type === "boolean") {
    return `<c r="${ref}" s="${styleId}" t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  return `<c r="${ref}" s="${styleId}" t="inlineStr"><is><t xml:space="preserve">${xml(value)}</t></is></c>`;
}

function rowXml(rowNumber, cells, height) {
  return `<row r="${rowNumber}"${height ? ` ht="${height}" customHeight="1"` : ""}>${cells.join("")}</row>`;
}

function dashboardSheet(report, chartCount) {
  const rowCells = new Map();
  const rowHeights = new Map();
  const merges = ["A1:L2", "A3:L3", "A4:L4", "A6:L6"];
  const addCell = (row, cell, height) => {
    if (!rowCells.has(row)) rowCells.set(row, []);
    if (cell) rowCells.get(row).push(cell);
    if (height) rowHeights.set(row, height);
  };
  addCell(1, cellXml(1, 0, report.title, 1), 28);
  addCell(2, "", 14);
  addCell(3, cellXml(3, 0, report.subtitle || report.organization || "", 2), 22);
  addCell(4, cellXml(4, 0, `${report.organization || ""}  |  تاريخ التصدير: ${report.generatedAt || ""}`, 3), 20);
  addCell(6, cellXml(6, 0, `نطاق التقرير: ${report.filterSummary || "جميع البيانات المصرح بها"}`, 17), 28);

  const kpis = Array.isArray(report.kpis) ? report.kpis.slice(0, 12) : [];
  kpis.forEach((kpi, index) => {
    const blockRow = 8 + Math.floor(index / 4) * 3;
    const startCol = (index % 4) * 3;
    const endCol = startCol + 2;
    merges.push(`${cellRef(blockRow, startCol)}:${cellRef(blockRow, endCol)}`);
    merges.push(`${cellRef(blockRow + 1, startCol)}:${cellRef(blockRow + 2, endCol)}`);
    const valueType = kpi.type || "integer";
    const valueStyle = valueType === "currency" ? 7 : valueType === "percentage" ? 8 : 16;
    addCell(blockRow, cellXml(blockRow, startCol, kpi.label, 15), 22);
    addCell(blockRow + 1, cellXml(blockRow + 1, startCol, kpi.value, valueStyle, valueType), 28);
  });

  const chartStart = 8 + Math.ceil(kpis.length / 4) * 3 + 1;
  const finalRow = chartCount
    ? chartStart + Math.ceil(chartCount / 2) * 16 + 4
    : chartStart + 4;
  addCell(finalRow, cellXml(finalRow, 0, "BY N9 LAW SYSTEM | NASSEH ZAHER ALNAMAN", 3), 20);
  merges.push(`A${finalRow}:L${finalRow}`);

  const rows = [...rowCells.keys()]
    .sort((left, right) => left - right)
    .map((row) => rowXml(row, rowCells.get(row), rowHeights.get(row)));

  const mergeXml = merges.length
    ? `<mergeCells count="${merges.length}">${merges.map((ref) => `<mergeCell ref="${ref}"/>`).join("")}</mergeCells>`
    : "";
  return {
    xml: worksheetXml({
      rows,
      dimension: `A1:L${finalRow}`,
      columns: Array.from({ length: 12 }, (_, index) => ({ index, width: 14.5 })),
      merges: mergeXml,
      drawing: chartCount > 0,
      landscape: true,
    }),
    chartStart,
  };
}

function dataSheet(sheet) {
  const columns = Array.isArray(sheet.columns) && sheet.columns.length
    ? sheet.columns.map(column => typeof column === 'string' ? {key:column,label:column,type:'text'} : column)
    : [{ key: "value", label: "القيمة", type: "text", width: 24 }];
  const sourceRows = Array.isArray(sheet.rows) ? sheet.rows : [];
  const headerRow = Math.max(3, Number(sheet.headerRow) || 4);
  const firstDataRow = headerRow + 1;
  const lastCol = colName(columns.length - 1);
  const lastRow = Math.max(headerRow, sourceRows.length + headerRow);
  const rows = [
    rowXml(1, [cellXml(1, 0, sheet.title || sheet.name, 18)], 28),
    rowXml(2, [cellXml(2, 0, sheet.description || "", 3)], 24),
    rowXml(headerRow, columns.map((column, index) => cellXml(headerRow, index, column.label, 4)), 28),
  ];
  sourceRows.forEach((item, rowIndex) => {
    const rowNumber = rowIndex + firstDataRow;
    const alternate = rowIndex % 2 === 1;
    rows.push(rowXml(
      rowNumber,
      columns.map((column, columnIndex) => {
        const value = Array.isArray(item) ? item[columnIndex] : item?.[column.key];
        return cellXml(rowNumber, columnIndex, value, styleFor(column.type, alternate), column.type);
      }),
      23,
    ));
  });
  const mergeXml = `<mergeCells count="2"><mergeCell ref="A1:${lastCol}1"/><mergeCell ref="A2:${lastCol}2"/></mergeCells>`;
  return worksheetXml({
    rows,
    dimension: `A1:${lastCol}${lastRow}`,
    columns: columns.map((column, index) => ({ index, width: Math.min(42, Math.max(11, column.width || 18)) })),
    merges: mergeXml,
    autoFilter: sourceRows.length ? `A${headerRow}:${lastCol}${lastRow}` : "",
    freezeRow: headerRow,
    landscape: columns.length > 7,
  });
}

function chartDataSheet(charts) {
  const rows = [];
  const index = [];
  let rowNumber = 1;
  charts.forEach((chart, chartIndex) => {
    const series = Array.isArray(chart.series) ? chart.series : [];
    const categories = Array.isArray(chart.categories) ? chart.categories : [];
    rows.push(rowXml(rowNumber, [cellXml(rowNumber, 0, chart.title || `رسم ${chartIndex + 1}`, 18)], 24));
    rowNumber += 1;
    const headerRow = rowNumber;
    rows.push(rowXml(headerRow, [
      cellXml(headerRow, 0, chart.categoryLabel || "الفئة", 4),
      ...series.map((item, seriesIndex) => cellXml(headerRow, seriesIndex + 1, item.name || `سلسلة ${seriesIndex + 1}`, 4)),
    ], 24));
    const firstDataRow = headerRow + 1;
    categories.forEach((category, categoryIndex) => {
      const currentRow = firstDataRow + categoryIndex;
      rows.push(rowXml(currentRow, [
        cellXml(currentRow, 0, category, styleFor("text", categoryIndex % 2 === 1), "text"),
        ...series.map((item, seriesIndex) => cellXml(
          currentRow,
          seriesIndex + 1,
          item.values?.[categoryIndex] ?? 0,
          styleFor(item.type === "percentage" ? "percentage" : "number", categoryIndex % 2 === 1),
          item.type === "percentage" ? "percentage" : "number",
        )),
      ], 22));
    });
    const lastDataRow = Math.max(firstDataRow, firstDataRow + categories.length - 1);
    index.push({ headerRow, firstDataRow, lastDataRow, seriesCount: series.length });
    rowNumber = lastDataRow + 3;
  });
  const maxSeries = Math.max(1, ...charts.map((chart) => chart.series?.length || 1));
  return {
    xml: worksheetXml({
      rows,
      dimension: `A1:${colName(maxSeries)}${Math.max(1, rowNumber)}`,
      columns: [
        { index: 0, width: 28 },
        ...Array.from({ length: maxSeries }, (_, position) => ({ index: position + 1, width: 16 })),
      ],
      landscape: false,
    }),
    index,
  };
}

function worksheetXml({ rows, dimension, columns, merges = "", autoFilter = "", freezeRow = 0, drawing = false, landscape = false }) {
  const cols = columns?.length
    ? `<cols>${columns.map((column) => `<col min="${column.index + 1}" max="${column.index + 1}" width="${column.width}" customWidth="1"/>`).join("")}</cols>`
    : "";
  const pane = freezeRow
    ? `<pane ySplit="${freezeRow}" topLeftCell="A${freezeRow + 1}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="A${freezeRow + 1}" sqref="A${freezeRow + 1}"/>`
    : "<selection activeCell=\"A1\" sqref=\"A1\"/>";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
  <worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
    <dimension ref="${dimension}"/><sheetViews><sheetView rightToLeft="1" showGridLines="0" workbookViewId="0">${pane}</sheetView></sheetViews>
    <sheetFormatPr defaultRowHeight="20"/>${cols}<sheetData>${rows.join("")}</sheetData>${merges}
    ${autoFilter ? `<autoFilter ref="${autoFilter}"/>` : ""}
    <pageMargins left="0.35" right="0.35" top="0.55" bottom="0.55" header="0.2" footer="0.2"/>
    <pageSetup orientation="${landscape ? "landscape" : "portrait"}" fitToWidth="1" fitToHeight="0" paperSize="9"/>
    ${drawing ? `<drawing r:id="rId1"/>` : ""}
  </worksheet>`;
}

function drawingXml(chartCount, startRow) {
  const anchors = Array.from({ length: chartCount }, (_, index) => {
    const columnSlot = index % 2;
    const rowSlot = Math.floor(index / 2);
    const fromCol = columnSlot * 6;
    const toCol = fromCol + 6;
    const fromRow = startRow - 1 + rowSlot * 16;
    const toRow = fromRow + 15;
    return `<xdr:twoCellAnchor>
      <xdr:from><xdr:col>${fromCol}</xdr:col><xdr:colOff>76200</xdr:colOff><xdr:row>${fromRow}</xdr:row><xdr:rowOff>76200</xdr:rowOff></xdr:from>
      <xdr:to><xdr:col>${toCol}</xdr:col><xdr:colOff>-76200</xdr:colOff><xdr:row>${toRow}</xdr:row><xdr:rowOff>-76200</xdr:rowOff></xdr:to>
      <xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${index + 2}" name="N9 Chart ${index + 1}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr><xdr:xfrm/>
      <a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${index + 1}"/></a:graphicData></a:graphic>
      </xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor>`;
  }).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">${anchors}</xdr:wsDr>`;
}

function chartFormula(sheetName, column, firstRow, lastRow) {
  const escaped = sheetName.replace(/'/g, "''");
  return `'${escaped}'!$${colName(column)}$${firstRow}:$${colName(column)}$${lastRow}`;
}

function cachedStringReference(formula, values) {
  return `<c:strRef><c:f>${xml(formula)}</c:f><c:strCache><c:ptCount val="${values.length}"/>${values.map((value, index) => `<c:pt idx="${index}"><c:v>${xml(value)}</c:v></c:pt>`).join("")}</c:strCache></c:strRef>`;
}

function cachedNumberReference(formula, values, percentage = false) {
  return `<c:numRef><c:f>${xml(formula)}</c:f><c:numCache><c:formatCode>${percentage ? "0.0%" : "#,##0"}</c:formatCode><c:ptCount val="${values.length}"/>${values.map((value, index) => `<c:pt idx="${index}"><c:v>${percentage ? Number(value || 0) / 100 : Number(value || 0)}</c:v></c:pt>`).join("")}</c:numCache></c:numRef>`;
}

function seriesXml(chart, source, sheetName) {
  const categories = chart.categories || [];
  return (chart.series || []).map((series, index) => {
    const color = String(series.color || CHART_COLORS[index % CHART_COLORS.length]).replace("#", "");
    const dataPoints = ["doughnut", "pie"].includes(chart.type)
      ? categories.map((_, pointIndex) => {
        const pointColor = CHART_COLORS[pointIndex % CHART_COLORS.length];
        return `<c:dPt><c:idx val="${pointIndex}"/><c:spPr><a:solidFill><a:srgbClr val="${pointColor}"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill></a:ln></c:spPr></c:dPt>`;
      }).join("")
      : "";
    const categoryFormula = chartFormula(sheetName, 0, source.firstDataRow, source.lastDataRow);
    const valueFormula = chartFormula(sheetName, index + 1, source.firstDataRow, source.lastDataRow);
    return `<c:ser><c:idx val="${index}"/><c:order val="${index}"/><c:tx><c:v>${xml(series.name || `سلسلة ${index + 1}`)}</c:v></c:tx>
      <c:spPr><a:solidFill><a:srgbClr val="${color}"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="${color}"/></a:solidFill></a:ln></c:spPr>
      ${dataPoints}
      <c:cat>${cachedStringReference(categoryFormula, categories)}</c:cat>
      <c:val>${cachedNumberReference(valueFormula, series.values || [], series.type === "percentage")}</c:val>
      ${chart.type === "line" ? `<c:marker><c:symbol val="circle"/><c:size val="5"/></c:marker><c:smooth val="0"/>` : ""}</c:ser>`;
  }).join("");
}

function chartXml(chart, source, chartIndex, sheetName) {
  const title = xml(chart.title || `رسم ${chartIndex + 1}`);
  const series = seriesXml(chart, source, sheetName);
  const axisA = 51000000 + chartIndex * 2;
  const axisB = axisA + 1;
  let plot;
  if (chart.type === "doughnut" || chart.type === "pie") {
    const tag = chart.type === "pie" ? "pieChart" : "doughnutChart";
    plot = `<c:${tag}><c:varyColors val="1"/>${series}<c:dLbls><c:showLegendKey val="0"/><c:showVal val="1"/><c:showCatName val="0"/><c:showPercent val="1"/><c:showLeaderLines val="1"/></c:dLbls>${chart.type === "doughnut" ? `<c:holeSize val="58"/>` : ""}</c:${tag}>`;
  } else {
    const isLine = chart.type === "line";
    const tag = isLine ? "lineChart" : "barChart";
    plot = `<c:${tag}>${isLine ? `<c:grouping val="standard"/>` : `<c:barDir val="col"/><c:grouping val="clustered"/>`}<c:varyColors val="0"/>${series}${!isLine ? `<c:gapWidth val="65"/>` : ""}<c:axId val="${axisA}"/><c:axId val="${axisB}"/></c:${tag}>
      <c:catAx><c:axId val="${axisA}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="b"/><c:tickLblPos val="nextTo"/><c:crossAx val="${axisB}"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/></c:catAx>
      <c:valAx><c:axId val="${axisB}"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="l"/><c:majorGridlines/><c:numFmt formatCode="#,##0" sourceLinked="0"/><c:tickLblPos val="nextTo"/><c:crossAx val="${axisA}"/><c:crosses val="autoZero"/><c:crossBetween val="between"/></c:valAx>`;
  }
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
  <c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
    <c:date1904 val="0"/><c:lang val="ar-SA"/><c:roundedCorners val="0"/>
    <c:chart><c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:r><a:rPr lang="ar-SA" sz="1200" b="1"/><a:t>${title}</a:t></a:r></a:p></c:rich></c:tx><c:layout/><c:overlay val="0"/></c:title>
    <c:autoTitleDeleted val="0"/><c:plotArea><c:layout/>${plot}</c:plotArea>
    <c:legend><c:legendPos val="b"/><c:layout/><c:overlay val="0"/></c:legend><c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/><c:showDLblsOverMax val="0"/></c:chart>
    <c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="${COLORS.line}"/></a:solidFill></a:ln></c:spPr>
  </c:chartSpace>`;
}

function stylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
  <styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
    <numFmts count="4"><numFmt numFmtId="164" formatCode="#,##0.00 &quot;ر.س&quot;"/><numFmt numFmtId="165" formatCode="0.0%"/><numFmt numFmtId="166" formatCode="dd/mm/yyyy"/><numFmt numFmtId="167" formatCode="dd/mm/yyyy hh:mm"/></numFmts>
    <fonts count="6">
      <font><sz val="10"/><color rgb="FF${COLORS.ink}"/><name val="Arial"/></font>
      <font><b/><sz val="20"/><color rgb="FFFFFFFF"/><name val="Arial"/></font>
      <font><sz val="9"/><color rgb="FF${COLORS.gray}"/><name val="Arial"/></font>
      <font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="Arial"/></font>
      <font><b/><sz val="10"/><color rgb="FF${COLORS.greenDark}"/><name val="Arial"/></font>
      <font><b/><sz val="17"/><color rgb="FF${COLORS.greenDark}"/><name val="Arial"/></font>
    </fonts>
    <fills count="7"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF${COLORS.green}"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FF${COLORS.greenLight}"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FF${COLORS.greenPale}"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FF${COLORS.goldLight}"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/><bgColor indexed="64"/></patternFill></fill></fills>
    <borders count="3"><border/><border><bottom style="thin"><color rgb="FF${COLORS.line}"/></bottom></border><border><left style="thin"><color rgb="FF${COLORS.line}"/></left><right style="thin"><color rgb="FF${COLORS.line}"/></right><top style="thin"><color rgb="FF${COLORS.line}"/></top><bottom style="thin"><color rgb="FF${COLORS.line}"/></bottom></border></borders>
    <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
    <cellXfs count="21">
      <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
      <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="right" vertical="center" readingOrder="2"/></xf>
      <xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="right" vertical="center" readingOrder="2"/></xf>
      <xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="right" vertical="center" readingOrder="2"/></xf>
      <xf numFmtId="0" fontId="3" fillId="2" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1" readingOrder="2"/></xf>
      <xf numFmtId="0" fontId="0" fillId="6" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center" wrapText="1" readingOrder="2"/></xf>
      <xf numFmtId="3" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="164" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="165" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="166" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="167" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center" wrapText="1" readingOrder="2"/></xf>
      <xf numFmtId="3" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="164" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="165" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="0" fontId="4" fillId="3" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" readingOrder="2"/></xf>
      <xf numFmtId="3" fontId="5" fillId="3" borderId="2" xfId="0" applyFont="1" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="0" fontId="2" fillId="4" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center" wrapText="1" readingOrder="2"/></xf>
      <xf numFmtId="0" fontId="4" fillId="5" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center" readingOrder="2"/></xf>
      <xf numFmtId="166" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
      <xf numFmtId="167" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    </cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles><dxfs count="0"/><tableStyles count="0" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleLight16"/>
  </styleSheet>`;
}

function contentTypes(sheetCount, chartCount) {
  const sheetOverrides = Array.from({ length: sheetCount }, (_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("");
  const chartOverrides = Array.from({ length: chartCount }, (_, index) => `<Override PartName="/xl/charts/chart${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${sheetOverrides}${chartCount ? `<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>` : ""}${chartOverrides}<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/></Types>`;
}

function workbookXml(sheets) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><fileVersion appName="N9"/><workbookPr date1904="0"/><bookViews><workbookView activeTab="0" firstSheet="0"/></bookViews><sheets>${sheets.map((sheet, index) => `<sheet name="${xml(sheet.name)}" sheetId="${index + 1}"${sheet.kind === "chartData" ? ' state="hidden"' : ""} r:id="rId${index + 1}"/>`).join("")}</sheets><calcPr calcId="191029" fullCalcOnLoad="1" forceFullCalc="1"/></workbook>`;
}

function workbookRels(sheetCount) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${Array.from({ length: sheetCount }, (_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join("")}<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
}

function rootRels() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>`;
}

function drawingRels(chartCount) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${Array.from({ length: chartCount }, (_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart${index + 1}.xml"/>`).join("")}</Relationships>`;
}

function coreProperties(report) {
  const now = new Date().toISOString();
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${xml(report.title)}</dc:title><dc:subject>N9 LAW SYSTEM report</dc:subject><dc:creator>N9 LAW SYSTEM</dc:creator><cp:lastModifiedBy>N9 LAW SYSTEM</cp:lastModifiedBy><dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified></cp:coreProperties>`;
}

function appProperties(sheets) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>N9 LAW SYSTEM</Application><DocSecurity>0</DocSecurity><ScaleCrop>false</ScaleCrop><HeadingPairs><vt:vector size="2" baseType="variant"><vt:variant><vt:lpstr>Worksheets</vt:lpstr></vt:variant><vt:variant><vt:i4>${sheets.length}</vt:i4></vt:variant></vt:vector></HeadingPairs><TitlesOfParts><vt:vector size="${sheets.length}" baseType="lpstr">${sheets.map((sheet) => `<vt:lpstr>${xml(sheet.name)}</vt:lpstr>`).join("")}</vt:vector></TitlesOfParts><Company>N9 Group</Company><LinksUpToDate>false</LinksUpToDate><SharedDoc>false</SharedDoc><HyperlinksChanged>false</HyperlinksChanged><AppVersion>1.0</AppVersion></Properties>`;
}

export function buildN9Xlsx(report) {
  const charts = Array.isArray(report.charts) ? report.charts.filter((chart) => chart?.categories?.length && chart?.series?.length) : [];
  const chartData = chartDataSheet(charts);
  const requestedSheets = Array.isArray(report.sheets) ? report.sheets : [];
  const sheets = uniqueSheetNames([
    { name: "لوحة المؤشرات", kind: "dashboard" },
    ...requestedSheets.map((sheet) => ({ ...sheet, kind: "data" })),
    ...(charts.length ? [{ name: "بيانات الرسوم", kind: "chartData" }] : []),
  ]);
  const dashboard = dashboardSheet(report, charts.length);
  const files = {
    "[Content_Types].xml": contentTypes(sheets.length, charts.length),
    "_rels/.rels": rootRels(),
    "docProps/core.xml": coreProperties(report),
    "docProps/app.xml": appProperties(sheets),
    "xl/workbook.xml": workbookXml(sheets),
    "xl/_rels/workbook.xml.rels": workbookRels(sheets.length),
    "xl/styles.xml": stylesXml(),
  };
  sheets.forEach((sheet, index) => {
    let content = dashboard.xml;
    if (sheet.kind === "data") content = dataSheet(sheet);
    if (sheet.kind === "chartData") content = chartData.xml;
    files[`xl/worksheets/sheet${index + 1}.xml`] = content;
  });
  if (charts.length) {
    files["xl/worksheets/_rels/sheet1.xml.rels"] = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/></Relationships>`;
    files["xl/drawings/drawing1.xml"] = drawingXml(charts.length, dashboard.chartStart);
    files["xl/drawings/_rels/drawing1.xml.rels"] = drawingRels(charts.length);
    charts.forEach((chart, index) => {
      files[`xl/charts/chart${index + 1}.xml`] = chartXml(chart, chartData.index[index], index, sheets.at(-1).name);
    });
  }
  return zipSync(
    Object.fromEntries(Object.entries(files).map(([name, value]) => [name, strToU8(value)])),
    { level: 6 },
  );
}

function htmlTable(section, limit = 1000) {
  const columns = section.columns || [];
  const rows = (section.rows || []).slice(0, limit);
  return `<section class="report-section"><div class="section-heading"><h2>${xml(section.title || section.name)}</h2><span>${rows.length} سجل</span></div>${section.description ? `<p class="section-description">${xml(section.description)}</p>` : ""}<table><thead><tr>${columns.map((column) => `<th>${xml(column.label)}</th>`).join("")}</tr></thead><tbody>${rows.map((item) => `<tr>${columns.map((column, index) => `<td>${xml(Array.isArray(item) ? item[index] : item?.[column.key] ?? "")}</td>`).join("")}</tr>`).join("")}</tbody></table></section>`;
}

function barChartHtml(chart) {
  const values = chart.series?.[0]?.values || [];
  const max = Math.max(1, ...values.map((value) => Number(value || 0)));
  return `<div class="print-chart"><h3>${xml(chart.title)}</h3>${(chart.categories || []).slice(0, 12).map((label, index) => `<div class="bar-row"><span>${xml(label)}</span><i><b style="width:${Math.max(2, (Number(values[index] || 0) / max) * 100)}%"></b></i><strong>${xml(values[index] ?? 0)}</strong></div>`).join("")}</div>`;
}

export function buildN9PrintableHtml(report) {
  const kpis = (report.kpis || []).map((kpi) => `<div><span>${xml(kpi.label)}</span><strong>${xml(kpi.displayValue ?? kpi.value ?? "-")}</strong></div>`).join("");
  const charts = (report.charts || []).slice(0, 4).map(barChartHtml).join("");
  const sections = (report.sheets || []).map((section) => htmlTable(section)).join("");
  const notes = (report.notes || []).map((note) => `<li>${xml(note)}</li>`).join("");
  return `<!doctype html><html lang="ar" dir="rtl"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${xml(report.title)}</title><style>
  :root{--green:#${COLORS.green};--ink:#${COLORS.ink};--line:#${COLORS.line};--pale:#${COLORS.greenPale};--gold:#${COLORS.gold}}
  *{box-sizing:border-box}body{font-family:Tahoma,Arial,sans-serif;color:var(--ink);margin:0;background:#eef3f0;line-height:1.6}.report{width:min(1180px,calc(100% - 28px));margin:24px auto;background:#fff;padding:34px;box-shadow:0 8px 30px #173c2c1c}.brand{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:4px solid var(--green);padding-bottom:18px}.brand-mark{display:grid;place-items:center;width:62px;height:62px;background:var(--green);color:white;font-weight:800;font-size:25px}.brand h1{margin:0;font-size:27px;color:var(--green)}.brand p,.meta{margin:4px 0;color:#66746e}.filter{margin:20px 0;padding:12px 16px;background:var(--pale);border-right:4px solid var(--gold)}.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:20px 0}.kpis div{border:1px solid var(--line);padding:14px;background:#fff}.kpis span{display:block;color:#66746e;font-size:12px}.kpis strong{display:block;margin-top:5px;color:var(--green);font-size:23px}.charts{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin:22px 0}.print-chart{border:1px solid var(--line);padding:14px;break-inside:avoid}.print-chart h3{font-size:15px;margin:0 0 12px}.bar-row{display:grid;grid-template-columns:130px 1fr 45px;gap:8px;align-items:center;margin:7px 0;font-size:11px}.bar-row span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.bar-row i{height:8px;background:#edf2ef;display:block}.bar-row b{height:100%;display:block;background:var(--green)}.bar-row strong{text-align:left}.report-section{margin-top:26px;break-before:auto}.section-heading{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid var(--green);padding-bottom:7px}.section-heading h2{font-size:18px;margin:0}.section-heading span{font-size:11px;color:#66746e}.section-description{color:#66746e;font-size:12px}table{width:100%;border-collapse:collapse;margin-top:10px;font-size:10px;table-layout:auto}th{background:var(--green);color:white;font-weight:700}th,td{padding:6px;border-bottom:1px solid var(--line);text-align:right;vertical-align:top}tbody tr:nth-child(even){background:var(--pale)}.notes{margin-top:24px;border:1px solid var(--line);padding:12px 18px}.footer{display:flex;justify-content:space-between;margin-top:28px;padding-top:12px;border-top:1px solid var(--line);color:#66746e;font-size:10px}.actions{position:sticky;bottom:12px;display:flex;justify-content:flex-start;margin-top:18px}.actions button{border:0;background:var(--green);color:#fff;padding:11px 18px;font-weight:700;cursor:pointer}@page{size:A4 landscape;margin:10mm}@media print{body{background:#fff}.report{width:100%;margin:0;padding:0;box-shadow:none}.actions{display:none}.report-section,.print-chart{break-inside:avoid}thead{display:table-header-group}.footer{position:fixed;bottom:0;left:0;right:0}.kpis{grid-template-columns:repeat(4,1fr)}}@media(max-width:800px){.kpis,.charts{grid-template-columns:1fr 1fr}.report{padding:18px}.brand{gap:12px}.bar-row{grid-template-columns:95px 1fr 35px}}
  </style></head><body><main class="report"><header class="brand"><div><h1>${xml(report.title)}</h1><p>${xml(report.subtitle || "")}</p><strong>${xml(report.organization || "")}</strong></div><div class="brand-mark">N9</div></header><div class="meta">تاريخ التصدير: ${xml(report.generatedAt || "")}</div><div class="filter"><strong>نطاق التقرير</strong><br>${xml(report.filterSummary || "جميع البيانات المصرح بها")}</div><div class="kpis">${kpis}</div><div class="charts">${charts}</div>${sections}${notes ? `<section class="notes"><strong>ملاحظات المنهج</strong><ul>${notes}</ul></section>` : ""}<footer class="footer"><span>BY N9 LAW SYSTEM</span><span>Developer: NASSEH ZAHER ALNAMAN</span></footer><div class="actions"><button type="button" onclick="window.print()">تنزيل PDF / طباعة</button></div></main></body></html>`;
}

export function xlsxResponse(bytes, filename) {
  const safe = String(filename || "N9-report.xlsx").replace(/[^A-Za-z0-9._-]/g, "-");
  return new Response(bytes, {
    status: 200,
    headers: {
      "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "content-disposition": `attachment; filename="${safe}"`,
      "cache-control": "private, no-store",
      "x-content-type-options": "nosniff",
    },
  });
}
